import { useState, type FormEvent } from "react";
import { Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { useI18n } from "../i18n";
import { SLUG_PATTERN, slugify } from "../lib/format";
import { navigate } from "../router/hash";
import { useSession } from "../state/session";

/** Platform admins open a new customer (tenant). Sites and members are added afterwards. */
export function NewTenantDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { provider, tenants, refreshUser } = useSession();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const effectiveSlug = slugEdited ? slug : slugify(name);
  const n = name.trim();
  const errors = {
    name: !n ? t("form.required") : n.length < 2 ? t("tenantForm.nameTooShort") : n.length > 120 ? t("form.tooLong", { max: 120 }) : null,
    slug: !SLUG_PATTERN.test(effectiveSlug)
      ? t("tenantForm.slugInvalid")
      : tenants.some((x) => x.slug === effectiveSlug)
        ? t("tenantForm.slugTaken")
        : null,
  };
  const shown = (k: keyof typeof errors) => (touched ? errors[k] : null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    setBusy(true);
    setError(null);
    try {
      const created = await provider.createTenant({ name: n, slug: effectiveSlug });
      await refreshUser(created.id);
      onClose();
      navigate("/sites");
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("tenantForm.title")}
      description={t("tenantForm.description")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("tenantForm.submit")}
          </button>
        </>
      }
    >
      <FormField id="tenant-name" label={t("tenantForm.name")} error={shown("name")}>
        <input {...fieldAria("tenant-name", { error: shown("name") })} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="organization" data-autofocus />
      </FormField>
      <FormField id="tenant-slug" label={t("tenantForm.slug")} hint={t("tenantForm.slugHint")} error={shown("slug")}>
        <input
          {...fieldAria("tenant-slug", { hint: true, error: shown("slug") })}
          className="mono"
          value={effectiveSlug}
          onChange={(e) => {
            setSlugEdited(true);
            setSlug(e.target.value.toLowerCase());
          }}
          maxLength={41}
          autoComplete="off"
          spellCheck={false}
        />
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
